import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import debounce from 'lodash/debounce';
import {generatePosterPreview, generatePosterPreviewWithBeacon} from '@Libraries/poster-library';

const DEBOUNCE_GENERATE_PREVIEW_DEFAULT_WAIT = 240000;
const DISABLE_LOCAL_STORAGE_KEY = 'dev_disable_poster_preview';

function isPosterPreviewDisabledLocally(): boolean {
  return localStorage.getItem(DISABLE_LOCAL_STORAGE_KEY) === '1';
}

export class PosterPreview {
  private readonly poster: Poster;
  private readonly debouncedGeneratePreview;
  private readonly boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

  constructor(poster: Poster) {
    this.poster = poster;
    this.debouncedGeneratePreview = debounce(() => {
      this.generatePreview();
    }, DEBOUNCE_GENERATE_PREVIEW_DEFAULT_WAIT);

    window.addEventListener('visibilitychange', this.boundHandleVisibilityChange);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.generatePreviewWithBeacon();
    }
  }

  public onDispose(): void {
    window.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
  }

  public triggerDebouncedGeneratePreview(): void {
    this.debouncedGeneratePreview();
  }

  /**
   * Generates right away instead of waiting out the debounce, so a newly created poster has a preview
   * even if the user leaves before the debounce fires or the visibility beacon is lost.
   */
  public triggerGeneratePreview(): void {
    this.debouncedGeneratePreview.cancel();
    this.generatePreview();
  }

  private generatePreview(): void {
    if (isPosterPreviewDisabledLocally()) return;
    if (!this.poster.mode.isGeneration()) {
      void generatePosterPreview(this.poster.hashedID).catch((e: unknown) => {
        console.error(e);
      });
    }
  }

  private generatePreviewWithBeacon(): void {
    if (isPosterPreviewDisabledLocally()) return;
    if (!this.poster.mode.isGeneration()) {
      generatePosterPreviewWithBeacon(this.poster.hashedID);
    }
  }
}
